/**
 * Phase 46 Plan 46-08 — Per-project Notifications Card on ProjectDetailPage.
 *
 * Composes the shared `NotificationRoutingMatrix` (from plan 46-07) with scope
 * `'project'` plus the add + edit + remove modals. Visibility-gated to
 * `(isAdmin || isProjectPM)` — non-PM team members do not see the Card at all
 * (mirrors Phase 38 / Phase 41 PM-gated section patterns; backend
 * `requireAdminOrProjectPM` is the security gate, this is just the UX gate).
 *
 * SPEC Req 7 — per-project Notifications tab.
 *
 * Reconciled at Wave-4 merge to match 46-07 component prop contracts:
 * `initialEventKind`/`initialChannel`/`initialTarget` on the editor modal,
 * separate `eventKind`/`channel` on the remove confirm modal.
 */
import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/common/Spinner';
import {
  NotificationRoutingMatrix,
  type MatrixRoute,
} from '../../components/notifications/NotificationRoutingMatrix';
import { NotificationRouteEditorModal } from '../../components/notifications/NotificationRouteEditorModal';
import { RemoveRouteConfirmModal } from '../../components/notifications/RemoveRouteConfirmModal';
import {
  useNotificationRoutes,
  useCreateNotificationRoute,
  useUpdateNotificationRoute,
  useDeleteNotificationRoute,
} from '../../hooks/useNotificationRoutes';
import { useAuth } from '../../services/authContext';
import type { EventKind, Channel } from '../../constants/notificationEnums';

interface Props {
  projectId: number;
  project: { projectManagerId: number | null };
}

interface EditorState {
  open: boolean;
  mode: 'create' | 'edit';
  routeId: number | null;
  eventKind: EventKind | null;
  channel: Channel | null;
  initialTarget: string | null;
}

const EDITOR_CLOSED: EditorState = {
  open: false,
  mode: 'create',
  routeId: null,
  eventKind: null,
  channel: null,
  initialTarget: null,
};

export function ProjectNotificationsTab({ projectId, project }: Props) {
  const { user, isAdmin } = useAuth();
  const isProjectPM = user?.id != null && project.projectManagerId === user.id;
  if (!isAdmin && !isProjectPM) return null;

  return <ProjectNotificationsTabInner projectId={projectId} />;
}

function ProjectNotificationsTabInner({ projectId }: { projectId: number }) {
  const { data: routes = [], isLoading } = useNotificationRoutes(projectId);
  const createMutation = useCreateNotificationRoute();
  const updateMutation = useUpdateNotificationRoute();
  const deleteMutation = useDeleteNotificationRoute();

  const [editor, setEditor] = useState<EditorState>(EDITOR_CLOSED);
  const [removeTarget, setRemoveTarget] = useState<MatrixRoute | null>(null);

  return (
    <Card title="Notification routing">
      <p className="text-[12px] text-ms-text-secondary mb-4">
        Choose which channels deliver each event kind for this project. Events without a
        per-project route fall back to the global defaults.
      </p>
      {isLoading ? (
        <Spinner />
      ) : (
        <NotificationRoutingMatrix
          routes={routes}
          scope="project"
          onAdd={(kind, channel) =>
            setEditor({
              open: true,
              mode: 'create',
              routeId: null,
              eventKind: kind,
              channel,
              initialTarget: null,
            })
          }
          onEdit={(route) =>
            setEditor({
              open: true,
              mode: 'edit',
              routeId: route.id,
              eventKind: route.eventKind,
              channel: route.channel,
              initialTarget: route.target,
            })
          }
          onRemove={(route) => setRemoveTarget(route)}
        />
      )}
      <NotificationRouteEditorModal
        open={editor.open}
        mode={editor.mode}
        scope="project"
        initialEventKind={editor.eventKind}
        initialChannel={editor.channel}
        initialTarget={editor.initialTarget}
        onSave={async (payload) => {
          if (editor.mode === 'create') {
            await createMutation.mutateAsync({ projectId, input: payload });
          } else if (editor.mode === 'edit' && editor.routeId != null) {
            await updateMutation.mutateAsync({
              projectId,
              routeId: editor.routeId,
              input: payload,
            });
          }
          setEditor(EDITOR_CLOSED);
        }}
        onClose={() => setEditor(EDITOR_CLOSED)}
      />
      <RemoveRouteConfirmModal
        open={removeTarget !== null}
        scope="project"
        eventKind={removeTarget?.eventKind ?? null}
        channel={removeTarget?.channel ?? null}
        onConfirm={async () => {
          if (removeTarget) {
            await deleteMutation.mutateAsync({ projectId, routeId: removeTarget.id });
          }
          setRemoveTarget(null);
        }}
        onClose={() => setRemoveTarget(null)}
      />
    </Card>
  );
}
